import { Modal, ModalBody, ModalDescription, ModalFooter, ModalHeader, ModalTitle } from "@/ds/custom/Modal";
import { MultiSelector } from "@/ds/custom/MultiSelector";
import { NumericInput } from "@/ds/custom/NumericInput";
import { Button } from "@/ds/shadcn/components/ui/button";
import { Checkbox } from "@/ds/shadcn/components/ui/checkbox";
import { Input } from "@/ds/shadcn/components/ui/input";
import { Label } from "@/ds/shadcn/components/ui/label";
import { Spinner } from "@/ds/shadcn/components/ui/spinner";
import { useCollectionCreate } from "@/features/collections/api/useCollectionCreate";
import { useCreatePrepopulatedCollection } from "@/features/collections/api/useCreatePrepopulatedCollection";
import { allFoilFlags, allRarityFlags, checkPrepopulateFlags, mergePrepopulateFlags, PrepopulateFilterFlags, removePrepopulateFlags } from "@/lib/domain/PrepopulateFilterFlags";
import { doesPrepopulateSetHaveColdFoils, doesPrepopulateSetHaveCommons, doesPrepopulateSetHaveFableds, doesPrepopulateSetHaveMarvels, doesPrepopulateSetHaveSuperRares, isPromoSet, PrepopulateSetOptions, type PrepopulateSet } from "@/lib/domain/PrepopulateSet";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { useNotifyMutationSuccess } from "@/lib/common/useNotifyMutationSuccess";
import { useEffect, useState, type FC } from "react";
import { useNavigate } from "react-router";
import { OptionBar } from "@/ds/custom/OptionBar";

export interface ModalCreateCollectionProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export const ModalCreateCollection: FC<ModalCreateCollectionProps> = props => {
	const { open, onOpenChange } = props;

	const navigate = useNavigate();
	const createCollection = useCollectionCreate();
	useNotifyMutationSuccess(createCollection, "Collection Created");

	const createPrepopulatedCollection = useCreatePrepopulatedCollection();
	useNotifyMutationSuccess(createPrepopulatedCollection, "Collection Created");

	const [name, setName] = useState("");
	const [prepopulated, setPrepopulated] = useState(false);
	const [prepopulateFlags, setPrepopulateFlags] = useState(allRarityFlags);
	const [sets, setSets] = useState<PrepopulateSet[]>([]);
	const [quantity, setQuantity] = useState(1);
	const [forceEquipmentWeaponQuantity, setForceEquipmentWeaponQuantity] = useState(true);
	const [forceColdFoilQuantity, setForceColdFoilQuantity] = useState(true);

	useEffect(() => {
		if (!open) {
			return;
		}

		setName("");
		setPrepopulated(false);
		setPrepopulateFlags(allRarityFlags);
		setSets([]);
		setQuantity(1);
		setForceEquipmentWeaponQuantity(true);
		setForceColdFoilQuantity(true);
	}, [open]);

	useEffect(() => {
		if (!createCollection.isSuccess) {
			return;
		}

		navigate("/collections/" + createCollection.data.body.collectionId);
	}, [createCollection.isSuccess]);

	useEffect(() => {
		if (!createPrepopulatedCollection.isSuccess) {
			return;
		}

		navigate("/collections/" + createPrepopulatedCollection.data.body.collectionId);
	}, [createPrepopulatedCollection.isSuccess]);

	const setsHaveColdFoils = sets.some(doesPrepopulateSetHaveColdFoils);
	const setsHaveSuperRares = sets.some(doesPrepopulateSetHaveSuperRares);
	const setsHaveMarvels = sets.some(doesPrepopulateSetHaveMarvels);
	const setsHavePromos = sets.some(isPromoSet);
	const setsHaveCommons = sets.some(doesPrepopulateSetHaveCommons);
	const setsHaveFableds = sets.some(doesPrepopulateSetHaveFableds);

	const validPrepopulateFlags = [
		[setsHaveColdFoils, PrepopulateFilterFlags.ColdFoil],
		[setsHaveSuperRares, PrepopulateFilterFlags.SuperRare],
		[setsHaveMarvels, PrepopulateFilterFlags.Marvel],
		[setsHavePromos, PrepopulateFilterFlags.Promo],
		[setsHaveCommons, PrepopulateFilterFlags.Common],
		[setsHaveFableds, PrepopulateFilterFlags.Fabled]
	].reduce(
		(flags, [hasFeature, flag]) => hasFeature ? flags : removePrepopulateFlags(flags, flag as number),
		prepopulateFlags
	);

	const onFlagChange = (flag: number) => (checked: boolean) => {
		setPrepopulateFlags(prev => checked ? mergePrepopulateFlags(prev, flag) : removePrepopulateFlags(prev, flag));
	};

	const onConfirm = () => {
		if (prepopulated) {
			createPrepopulatedCollection.mutate({
				name,
				sets,
				filterFlags: validPrepopulateFlags,
				quantity,
				forceEquipmentWeaponQuantity,
				forceColdFoilQuantity
			});
		} else {
			createCollection.mutate({ name });
		}
	};

	const hasSelectedFoiling = (validPrepopulateFlags & allFoilFlags) !== PrepopulateFilterFlags.None;
	const hasSelectedRarity = (validPrepopulateFlags & allRarityFlags) !== PrepopulateFilterFlags.None;
	const isLoading = createCollection.isPending || createPrepopulatedCollection.isPending;
	const confirmDisabled = !name.trim() || isLoading || (prepopulated && (sets.length === 0 || !hasSelectedFoiling || !hasSelectedRarity));
	const error = createCollection.error ?? createPrepopulatedCollection.error;

	return (
		<Modal open={open} onOpenChange={onOpenChange} preventCloseOnInteractOutside={isLoading}>
			<ModalHeader>
				<ModalTitle>Create Collection</ModalTitle>
				<ModalDescription>Enter a name and choose how to create your new collection.</ModalDescription>
			</ModalHeader>
			<ModalBody>
				<div className="flex flex-col gap-4">
					<OptionBar
						options={[{ label: "Empty", value: "empty" }, { label: "Pre-populated", value: "prepopulated" }]}
						value={prepopulated ? "prepopulated" : "empty"}
						onChange={value => setPrepopulated(value === "prepopulated")} />

					<div className="flex flex-col gap-2">
						<Label>Name</Label>
						<Input
							placeholder="Collection name"
							value={name}
							onChange={e => setName(e.target.value)}
							onKeyDown={e => e.key === "Enter" && !prepopulated && !confirmDisabled && onConfirm()} />
					</div>

					{
						prepopulated &&
						<>
							<div className="flex flex-col gap-2">
								<Label>Sets</Label>
								<MultiSelector
									placeholder="Select sets"
									options={PrepopulateSetOptions}
									values={sets}
									onChange={setSets} />
							</div>

							<div className="flex flex-col gap-2">
								<Label>Foiling</Label>
								<div className="flex flex-wrap gap-x-4 gap-y-2">
									<label className="flex items-center gap-2 text-sm cursor-pointer">
										<Checkbox
											checked={checkPrepopulateFlags(validPrepopulateFlags, PrepopulateFilterFlags.ColdFoil)}
											onCheckedChange={onFlagChange(PrepopulateFilterFlags.ColdFoil)}
											disabled={!setsHaveColdFoils} />
										Cold Foils
									</label>
									<label className="flex items-center gap-2 text-sm cursor-pointer">
										<Checkbox
											checked={checkPrepopulateFlags(validPrepopulateFlags, PrepopulateFilterFlags.RainbowFoil)}
											onCheckedChange={onFlagChange(PrepopulateFilterFlags.RainbowFoil)} />
										Rainbow Foils
									</label>
									<label className="flex items-center gap-2 text-sm cursor-pointer">
										<Checkbox
											checked={checkPrepopulateFlags(validPrepopulateFlags, PrepopulateFilterFlags.NonFoil)}
											onCheckedChange={onFlagChange(PrepopulateFilterFlags.NonFoil)} />
										Non Foils
									</label>
								</div>
							</div>

							<div className="flex flex-col gap-2">
								<Label>Rarity</Label>
								<div className="flex flex-wrap gap-x-4 gap-y-2">
									{
										setsHaveCommons &&
										<label className="flex items-center gap-2 text-sm cursor-pointer">
											<Checkbox
												checked={checkPrepopulateFlags(validPrepopulateFlags, PrepopulateFilterFlags.Common)}
												onCheckedChange={onFlagChange(PrepopulateFilterFlags.Common)} />
											Common
										</label>
									}
									<label className="flex items-center gap-2 text-sm cursor-pointer">
										<Checkbox
											checked={checkPrepopulateFlags(validPrepopulateFlags, PrepopulateFilterFlags.Rare)}
											onCheckedChange={onFlagChange(PrepopulateFilterFlags.Rare)} />
										Rare
									</label>
									{
										setsHaveSuperRares &&
										<label className="flex items-center gap-2 text-sm cursor-pointer">
											<Checkbox
												checked={checkPrepopulateFlags(validPrepopulateFlags, PrepopulateFilterFlags.SuperRare)}
												onCheckedChange={onFlagChange(PrepopulateFilterFlags.SuperRare)} />
											Super Rare
										</label>
									}
									<label className="flex items-center gap-2 text-sm cursor-pointer">
										<Checkbox
											checked={checkPrepopulateFlags(validPrepopulateFlags, PrepopulateFilterFlags.Majestic)}
											onCheckedChange={onFlagChange(PrepopulateFilterFlags.Majestic)} />
										Majestic
									</label>
									{
										setsHaveMarvels &&
										<label className="flex items-center gap-2 text-sm cursor-pointer">
											<Checkbox
												checked={checkPrepopulateFlags(validPrepopulateFlags, PrepopulateFilterFlags.Marvel)}
												onCheckedChange={onFlagChange(PrepopulateFilterFlags.Marvel)} />
											Marvel
										</label>
									}
									<label className="flex items-center gap-2 text-sm cursor-pointer">
										<Checkbox
											checked={checkPrepopulateFlags(validPrepopulateFlags, PrepopulateFilterFlags.Legendary)}
											onCheckedChange={onFlagChange(PrepopulateFilterFlags.Legendary)} />
										Legendary
									</label>
									{
										setsHaveFableds &&
										<label className="flex items-center gap-2 text-sm cursor-pointer">
											<Checkbox
												checked={checkPrepopulateFlags(validPrepopulateFlags, PrepopulateFilterFlags.Fabled)}
												onCheckedChange={onFlagChange(PrepopulateFilterFlags.Fabled)} />
											Fabled
										</label>
									}
									{
										setsHavePromos &&
										<label className="flex items-center gap-2 text-sm cursor-pointer">
											<Checkbox
												checked={checkPrepopulateFlags(validPrepopulateFlags, PrepopulateFilterFlags.Promo)}
												onCheckedChange={onFlagChange(PrepopulateFilterFlags.Promo)} />
											Promo
										</label>
									}
								</div>
							</div>

							<div className="flex flex-col gap-2">
								<Label>Quantity Per Card</Label>
								<NumericInput
									value={quantity}
									onChange={setQuantity}
									maxDecimalPlaces={0}
									minValue={1}
									maxValue={10000} />
							</div>

							<div className="flex flex-col gap-2">
								<label className="flex items-center gap-2 text-sm cursor-pointer">
									<Checkbox
										checked={forceEquipmentWeaponQuantity && quantity !== 1}
										onCheckedChange={checked => setForceEquipmentWeaponQuantity(!!checked)}
										disabled={quantity === 1} />
									Force Equipment and Weapons to have quantity of one
								</label>
								<label className="flex items-center gap-2 text-sm cursor-pointer">
									<Checkbox
										checked={forceColdFoilQuantity && quantity !== 1 && setsHaveColdFoils && checkPrepopulateFlags(validPrepopulateFlags, PrepopulateFilterFlags.ColdFoil)}
										onCheckedChange={checked => setForceColdFoilQuantity(!!checked)}
										disabled={quantity === 1 || !setsHaveColdFoils || !checkPrepopulateFlags(validPrepopulateFlags, PrepopulateFilterFlags.ColdFoil)} />
									Force cold foils to have quantity of one
								</label>
							</div>
						</>
					}

					<ErrorDisplay error={error} />
				</div>
			</ModalBody>
			<ModalFooter>
				<Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
					Cancel
				</Button>
				<Button onClick={onConfirm} disabled={confirmDisabled}>
					{isLoading ? <Spinner /> : <>Create</>}
				</Button>
			</ModalFooter>
		</Modal>
	);
};
